<template>
  <section class="gm-billing" aria-label="Configuración del día de facturación">
    <header class="gm-billing__header">
      <div class="gm-billing__heading">
        <span class="gm-billing__icon" aria-hidden="true"><i class="fa-regular fa-calendar-check"></i></span>
        <div>
          <h6 class="gm-billing__title">Día de facturación</h6>
          <p class="gm-billing__subtitle">Cambia el inicio de los periodos de cobro. Los anteriores permanecen intactos.
          </p>
        </div>
      </div>
      <span v-if="record && record.has_custom_schedule" class="gm-billing__state">Personalizado</span>
      <span v-else-if="record" class="gm-billing__state gm-billing__state--neutral">Original</span>
    </header>

    <div v-if="loading" class="gm-billing__notice" role="status">
      <i class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>Consultando calendario de facturación…
    </div>
    <div v-else-if="loadError" class="gm-billing__notice gm-billing__notice--error" role="alert">
      {{ loadError }}
      <button type="button" class="gm-billing__text-button" @click="fetchSchedule">Reintentar</button>
    </div>
    <template v-else-if="record">
      <div class="gm-billing__facts">
        <div class="gm-billing__fact">
          <span>Inicio del contrato</span>
          <strong>{{ formatDate(record.start_date) }}</strong>
        </div>
        <div class="gm-billing__fact">
          <span>Día original</span>
          <strong>{{ record.original_billing_day }}</strong>
        </div>
        <div class="gm-billing__fact">
          <span>Último día configurado</span>
          <strong>{{ record.configured_billing_day }}</strong>
        </div>
      </div>

      <div v-if="!supportedPeriod" class="gm-billing__notice" role="note">
        Esta recurrencia conserva su calendario original. El cambio de día fijo solo aplica a periodos mensuales,
        bimestrales, trimestrales, cuatrimestrales, semestrales y anuales.
      </div>

      <div v-else-if="canEdit" class="gm-billing__editor">
        <div class="gm-billing__field">
          <label :for="selectId">Nuevo día de facturación</label>
          <select :id="selectId" v-model.number="selectedDay" class="form-control" :disabled="saving || previewing">
            <option v-for="n in 31" :key="n" :value="n">Día {{ n }} de cada mes aplicable</option>
          </select>
          <small>Para meses cortos se usará el último día disponible.</small>
        </div>
        <div class="gm-billing__field gm-billing__field--reason">
          <label :for="reasonId">Motivo (opcional)</label>
          <input :id="reasonId" v-model.trim="reason" class="form-control" type="text" maxlength="500"
            :disabled="saving || previewing" placeholder="Ej. Solicitud del arrendatario" />
        </div>
        <div class="gm-billing__buttons">

          <button type="button" class="btn btn-outline-primary btn-sm m-1" :disabled="previewing || saving"
            @click="getPreview">
            <i :class="previewing
              ? 'fa fa-spinner fa-spin'
              : 'fa fa-calendar-alt'"></i>

            {{ previewing ? 'Calculando…' : 'Simular cambio' }}
          </button>

          <button type="button" class="btn btn-primary btn-sm m-1" :disabled="!canSave" @click="confirmChange">
            <i :class="saving
              ? 'fa fa-spinner fa-spin'
              : 'fa fa-save'"></i>

            {{ saving ? 'Guardando…' : 'Guardar cambio' }}
          </button>

        </div>
        <p v-if="actionError" class="gm-billing__notice gm-billing__notice--error" role="alert">{{ actionError }}</p>

        <div v-if="preview" class="gm-billing__preview" aria-live="polite">
          <div class="gm-billing__preview-heading">
            <i class="fa-regular fa-calendar-check" aria-hidden="true"></i>
            <strong>Vista previa de nuevos periodos — sin cambios guardados</strong>
          </div>
          <dl>
            <template v-if="preview.transition">
              <div>
                <dt>Periodo de transición</dt>
                <dd>{{ formatDate(preview.transition.period_start) }} – {{ formatDate(preview.transition.period_end) }}
                </dd>
              </div>
              <div>
                <dt>Días de transición</dt>
                <dd>{{ preview.transition.days }} de {{ preview.transition.old_cycle_days }} días del ciclo original
                </dd>
              </div>
              <div>
                <dt>Fecha de cobro de transición</dt>
                <dd>{{ formatDate(preview.transition.billing_date) }}</dd>
              </div>
              <div>
                <dt>Factor de prorrateo orientativo</dt>
                <dd>{{ preview.transition.proration_fraction }}</dd>
              </div>
            </template>
            <div>
              <dt>Primer periodo nuevo completo</dt>
              <dd><strong>{{ formatDate(preview.period_start) }} – {{ formatDate(preview.period_end) }}</strong></dd>
            </div>
            <div>
              <dt>Primera fecha de facturación nueva</dt>
              <dd><strong>{{ formatDate(preview.next_billing_date) }}</strong></dd>
            </div>
            <div>
              <dt>Adeudos anteriores modificados</dt>
              <dd>{{ preview.existing_dues_modified }}</dd>
            </div>
          </dl>
          <div class="gm-billing__notice" role="note">
            Los nuevos periodos serán contiguos, sin días repetidos. El importe de transición
            requiere validar prorrateos de renta y servicios. El servidor debe validar los importes de transición antes
            de
            guardar.
            La simulación no garantiza que el cambio pueda confirmarse.
          </div>
        </div>
      </div>
      <div v-else class="gm-billing__notice" role="note">La actualización del día de facturación está disponible
        solamente
        para administradores autorizados.</div>

      <details v-if="record.history && record.history.length" class="gm-billing__history">
        <summary>Historial de cambios ({{ record.history.length }})</summary>
        <div class="gm-billing__table-wrap">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>Nuevo día</th>
                <th>Primer ciclo</th>
                <th>Motivo</th>
                <th>Registrado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in record.history" :key="item.id">
                <td>Día {{ item.billing_day }}</td>
                <td>{{ formatDate(item.effective_cycle_start) }}</td>
                <td>{{ item.reason || '—' }}</td>
                <td>{{ formatDate(item.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </template>
  </section>
</template>

<script>
import request from '@/services/request';

export default {
  name: 'ContractBillingDay',
  props: {
    contractId: { type: [Number, String], required: true },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      record: null,
      selectedDay: 1,
      reason: '',
      preview: null,
      previewing: false,
      saving: false,
      loading: false,
      loadError: '',
      actionError: '',
      requestSequence: 0,
    };
  },
  computed: {
    selectId() { return `gm-billing-day-${this._uid}`; },
    reasonId() { return `gm-billing-reason-${this._uid}`; },
    supportedPeriod() {
      return this.record && [1, 2, 3, 4, 8, 9].includes(Number(this.record.payment_period_id));
    },
    canSave() {
      return Boolean(
        this.canEdit &&
        this.supportedPeriod &&
        this.record &&
        this.preview &&
        Number(this.preview.billing_day) === Number(this.selectedDay) &&
        this.preview.effective_cycle_start &&
        this.preview.next_billing_date &&
        !this.saving &&
        !this.previewing &&
        !this.loading
      );
    },
  },
  watch: {
    contractId: {
      immediate: true,
      handler() { this.fetchSchedule(); },
    },
    selectedDay() { this.preview = null; this.actionError = ''; },
  },
  beforeDestroy() { this.requestSequence += 1; },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      // Evita desfases UTC de new Date('YYYY-MM-DD') en navegadores.
      const plain = String(value).slice(0, 10);
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(plain);
      return match ? `${match[3]}/${match[2]}/${match[1]}` : plain;
    },
    apiError(error, fallback) {
      const response = error && error.response;
      const data = response && response.data;
      if (data && data.errors) {
        const key = Object.keys(data.errors)[0];
        const messages = data.errors[key];
        if (Array.isArray(messages) && messages.length) return String(messages[0]);
      }
      if (response && response.status === 403) return 'No cuentas con permiso para consultar o cambiar este calendario.';
      if (response && response.status === 401) return 'La sesión terminó. Vuelve a iniciar sesión.';
      if (data && data.message) return String(data.message);
      return fallback;
    },
    async fetchSchedule() {
      const id = Number(this.contractId);
      const seq = ++this.requestSequence;
      this.record = null;
      this.preview = null;
      this.saving = false;
      this.previewing = false;
      this.loadError = '';
      this.actionError = '';
      if (!Number.isInteger(id) || id <= 0) return;
      this.loading = true;
      try {
        const response = await request.get(`/contracts/${id}/billing-schedule`);
        if (seq !== this.requestSequence) return;
        this.record = response.data.data;
        this.selectedDay = Number(this.record.configured_billing_day || this.record.original_billing_day);
      } catch (error) {
        if (seq === this.requestSequence) this.loadError = this.apiError(error, 'No se pudo consultar la configuración.');
      } finally {
        if (seq === this.requestSequence) this.loading = false;
      }
    },
    async getPreview() {
      if (!this.record || !this.canEdit || !this.supportedPeriod) return;
      this.actionError = '';
      this.preview = null;
      this.previewing = true;
      const id = Number(this.contractId);
      const seq = this.requestSequence;
      try {
        const response = await request.post(`/contracts/${id}/billing-schedule/preview`, {
          billing_day: Number(this.selectedDay),
        });
        if (seq !== this.requestSequence) return;
        this.preview = response.data.data;
      } catch (error) {
        if (seq === this.requestSequence) this.actionError = this.apiError(error, 'No se pudo simular el cambio.');
      } finally {
        if (seq === this.requestSequence) this.previewing = false;
      }
    },
    async confirmChange() {
      if (!this.canSave) return;
      if (!this.$swal) {
        this.actionError = 'No se encontró el diálogo de confirmación. No se ha guardado el cambio.';
        return;
      }
      const p = this.preview;
      const transition = p.transition;
      const transitionText = transition
        ? `${this.formatDate(transition.period_start)} al ${this.formatDate(transition.period_end)}`
        : 'No se requiere transición';
      const message = [
        `Día anterior: ${Number(p.previous_billing_day)}`,
        `Nuevo día: ${Number(p.billing_day)}`,
        `Transición: ${transitionText}`,
        `Primer nuevo periodo: ${this.formatDate(p.period_start)} al ${this.formatDate(p.period_end)}`,
        'Los adeudos emitidos no deben modificarse.',
        'La simulación NO implica autorización para guardar. Laravel validará y puede rechazar la operación.'
      ].join('\n');
      const confirmation = await this.$swal({
        title: '¿Confirmar cambio de facturación?',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirmar y guardar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2563eb',
        reverseButtons: true,
      });
      if (!confirmation.isConfirmed) return;
      this.actionError = '';
      this.saving = true;
      const id = Number(this.contractId);
      const seq = this.requestSequence;
      try {
        const payload = {
          billing_day: Number(this.preview.billing_day),
          reason: this.reason || null,
          expected_effective_cycle_start: this.preview.effective_cycle_start,
          expected_next_billing_date: this.preview.next_billing_date,
        };
        // request es la abstracción del proyecto. Axios suele exponer patch();
        // si el wrapper no la soporta, usar su post() con method spoofing Laravel.
        const response = typeof request.patch === 'function'
          ? await request.patch(`/contracts/${id}/billing-day`, payload)
          : await request.post(`/contracts/${id}/billing-day`, { ...payload, _method: 'PATCH' });
        if (seq !== this.requestSequence) return;
        const result = response.data.data;
        this.preview = null;
        this.reason = '';
        await this.fetchSchedule();
        if (this.$swal) this.$swal({
          icon: 'success', title: 'Cambio programado',
          text: `El primer ciclo aplicable comienza el ${this.formatDate(result.effective_cycle_start)}.`
        });
        this.$emit('billing-updated', result);
      } catch (error) {
        if (seq === this.requestSequence) {
          this.preview = null; // exigir nueva simulación ante cambio concurrente
          this.actionError = this.apiError(error, 'No se pudo guardar. Simula nuevamente.');
          if (this.$swal) {
            await this.$swal({ icon: 'error', title: 'Cambio no guardado', text: this.actionError });
          }
        }
      } finally {
        if (Number(this.contractId) === id) this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.gm-billing {
  width: 100%;
  margin: 18px 0 16px;
  padding: 19px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #fff;
  color: #233047;
  box-shadow: 0 2px 12px rgba(25, 42, 65, .035);
}

.gm-billing__header,
.gm-billing__heading,
.gm-billing__preview-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gm-billing__header {
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.gm-billing__icon {
  width: 39px;
  height: 39px;
  border-radius: 11px;
  background: #eef5ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex-shrink: 0;
}

.gm-billing__title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -.01em;
}

.gm-billing__subtitle {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.gm-billing__state {
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  padding: 5px 9px;
  color: #1d4ed8;
  background: #eff6ff;
}

.gm-billing__state--neutral {
  color: #475569;
  background: #f1f5f9;
}

.gm-billing__facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.gm-billing__fact {
  min-height: 65px;
  background: #f8fafc;
  border: 1px solid #f0f3f8;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 11px 13px;
}

.gm-billing__fact span {
  font-size: 11px;
  color: #64748b;
}

.gm-billing__fact strong {
  font-size: 14px;
  color: #0f172a;
}

.gm-billing__editor {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(220px, 2fr);
  gap: 12px 16px;
  align-items: normal;
  margin-top: 14px;
}

.gm-billing__field label {
  display: block;
  font-weight: 600;
  margin-bottom: 5px;
  font-size: 12px;
}

.gm-billing__field small {
  display: block;
  color: #64748b;
  margin-top: 4px;
  font-size: 11px;
}

.gm-billing__field .form-control {
  min-height: 37px;
  border-radius: 8px;
}

.gm-billing__buttons {
  grid-column: 1 / -1;
}

.gm-billing__buttons .btn,
.gm-billing__preview .btn {
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 13px;
}

.gm-billing__notice {
  margin: 10px 0 0;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  font-size: 12px;
  color: #334155;
}

.gm-billing__notice--error {
  color: #9f1239;
  background: #fff5f6;
  border-color: #fecdd3;
  grid-column: 1 / -1;
}

.gm-billing__text-button {
  display: inline;
  border: 0;
  color: #1d4ed8;
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;
}

.gm-billing__preview {
  grid-column: 1 / -1;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 14px;
  background: #f8fbff;
}

.gm-billing__preview-heading {
  color: #1e40af;
  font-size: 12px;
  margin-bottom: 8px;
}

.gm-billing__preview dl {
  margin: 0;
}

.gm-billing__preview dl div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.gm-billing__preview dt {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.gm-billing__preview dd {
  margin: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
}

.gm-billing__help {
  font-size: 11px;
  margin: 12px 0;
  color: #475569;
  line-height: 1.5;
}

.gm-billing__history {
  margin-top: 15px;
  border-top: 1px solid #e2e8f0;
  padding-top: 13px;
  font-size: 12px;
}

.gm-billing__history summary {
  cursor: pointer;
  font-weight: 600;
  color: #334155;
}

.gm-billing__table-wrap {
  overflow-x: auto;
  margin-top: 10px;
}

.gm-billing__history table {
  font-size: 11px;
  min-width: 470px;
}

@media(max-width: 767px) {
  .gm-billing {
    padding: 14px;
  }

  .gm-billing__facts {
    grid-template-columns: 1fr;
  }

  .gm-billing__editor {
    grid-template-columns: 1fr;
  }

  .gm-billing__preview dl div {
    display: block;
  }

  .gm-billing__preview dd {
    text-align: left;
    margin-top: 4px;
  }
}
</style>
